import ImageComponent from '@/components/Layout/Image';
import type { EditorialBanner, EditorialPartner } from '@/pages/[categorias]/[...news]';
import classNames from '@/utils/className';

interface EditorialPartnersBannerProps {
  editoria?: EditorialBanner | null;
  parceiros: EditorialPartner[];
  className?: string;
}

// Cor de fundo padrão quando a editoria não define `cor` no Directus. Tom escuro
// (igual ao CT Eletro legado) para manter legível o rótulo claro "Conteúdo
// apoiado por:". Editorias devem cadastrar a cor de marca em Editorias.cor.
const COR_PADRAO = '#1C1C1C';

// Garante protocolo no link do parceiro: o cadastro no Directus aceita URLs sem
// esquema (ex.: "www.kabum.com.br"), que o browser interpretaria como rota
// relativa. Prefixa https:// quando não houver protocolo.
const normalizePartnerUrl = (link: string): string => (/^https?:\/\//i.test(link) ? link : `https://${link}`);

/**
 * Banner/tarja da editoria da matéria (modelo Directus): caixa com a cor de
 * marca da editoria + logo da editoria à esquerda. Quando há parceiros
 * cadastrados, exibe também "Conteúdo apoiado por:" + logos à direita.
 *
 * Único banner de editoria da página interna (não há mais caminho hardcoded por
 * marca). A tarja identifica a editoria pela LOGO: sem logo cadastrada no
 * Directus → editoria sem identidade visual → não renderiza tarja, independente
 * de haver parceiro. Isso evita a "tarja preta sem logo" das editorias novas.
 */
export default function EditorialPartnersBanner({ editoria, parceiros, className }: EditorialPartnersBannerProps) {
  const comLogo = (parceiros ?? []).filter(parceiro => parceiro.logo?.url);
  const editoriaLogo = editoria?.logo?.url;

  if (!editoriaLogo) return null;

  const cor = editoria?.cor || COR_PADRAO;

  return (
    <div
      className={classNames(
        'isInternal flex flex-col items-center justify-center gap-4 rounded-t-lg rounded-b-none p-4 tablet:flex-row tablet:justify-between tablet:pb-6 [&>a]:h-8',
        className
      )}
      style={{ backgroundColor: cor }}
    >
      {editoriaLogo && (
        <a className="flex items-center" href={`/${editoria.slug}/`} title={editoria.nome}>
          <ImageComponent src={editoriaLogo} alt={editoria.nome} height={33} width={108} objectFit="object-contain" rounded={false} />
        </a>
      )}
      {comLogo.length > 0 && (
        <span
          data-before="Conteúdo apoiado por:"
          className="flex flex-col text-sm before:block before:text-mglGray50 before:content-[attr(data-before)]"
        >
          <div className="mt-2 flex flex-wrap justify-center gap-4">
            {comLogo.map(parceiro => {
              const logo = (
                <ImageComponent
                  src={parceiro.logo.url}
                  alt={parceiro.nome}
                  height={33}
                  width={108}
                  objectFit="object-contain"
                  rounded={false}
                />
              );

              if (!parceiro.link) {
                return (
                  <span key={parceiro.id} className="z-[2] flex items-center">
                    {logo}
                  </span>
                );
              }

              return (
                <a
                  key={parceiro.id}
                  className="z-[2] flex items-center"
                  href={normalizePartnerUrl(parceiro.link)}
                  rel="sponsored"
                  title={`Ir para o site de ${parceiro.nome}`}
                  target="_blank"
                >
                  {logo}
                </a>
              );
            })}
          </div>
        </span>
      )}
    </div>
  );
}
